<template>
  <div>

    <div class="row mt-4">
      <div class="col-md-6">
        <input v-model="query.generic" class="input form-control"
               @keypress.enter="find()"
               placeholder="Pesquisar por: Chave, Numero ou CNPJ">
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <div class="col-md-6">
        <button class="btn btn-outline px-3 mr-2"
                :class="{ 'btn-outline-active': showFilter }"
                @click="showFilter = !showFilter">
          Filtros
          <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': showFilter }"/>
        </button>

        <button v-if="changeQuery" @click="find()" class="btn btn-link">
          Aplicar filtros ({{ numberOfBadges }})
        </button>
      </div>
    </div>

    <!-- Badge of filters -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters :filters="[filterOrigem, filterStartDate, filterEndDate]"
                      :query="query"
                      :amountChanged="updateNumberOfBadges"
                      @clearAllBadges="$nextTick(() => find())"
                      @clearBadge="$nextTick(() => find())"/>
      </div>
    </div>

    <div v-show="showFilter">
      <div class="row mt-4">
        <div class="col-md-3">
          <label>Origem</label>
          <Select2 :options="filterOrigem" v-model="query.origem"/>
        </div>

        <div class="col-md-3">
          <label for="nfse-type-period">Tipo do período</label>
          <select v-model="query.periodType" id="nfse-type-period" class="browser-default custom-select">
            <option value="data_emissao">Data de emissão</option>
            <option value="created_at">Data de criação</option>
            <option value="updated_at">Data de atualização</option>
          </select>
        </div>

        <div class="col-md-3">
          <label for="nfse-start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" class="form-control" id="nfse-start-date">
        </div>

        <div class="col-md-3">
          <label for="nfse-end-date">Data final</label>
          <input v-model="query.endDate" type="date" class="form-control" id="nfse-end-date">
        </div>
      </div>
    </div>

    <hr>

    <!-- Buttons -->
    <div class="row mt-2">
      <div class="col-md-12">
        <Dropdown title-dropdown="Download"
                  class="btn btn-primary mt-1 mr-1"
                  :enable="enableDownload">
          <ul class="mt-2">
            <li class="bkps-dropdown-item ml-2" @click="generateReport">
              Gerar Relatório
            </li>
            <li class="bkps-dropdown-item ml-2" @click="downloadXmlBatch">
              Baixar XML
            </li>
          </ul>
        </Dropdown>

        <button class="btn btn-primary mt-1 mr-1" @click="$router.push('/dfes')">
          <repeat-icon size="1x" class="mr-1"/> DF-e (todos documentos)
        </button>
      </div>
    </div>

    <!-- Info -->
    <div class="row mt-4">
      <div class="col-md-6 mt-2">
        <strong>{{ infoTotalModels }}</strong>
        <button class="btn btn-outline ml-3 mb-1"
                v-if="nfseNacionais.length > 0"
                @click="changeCheckAll"
                :class="{ 'btn-outline-active': checkAll }">
          {{ !checkAll ? 'Selecionar todas' : 'Desmarcar todas' }} ({{ meta.totalCount || 'Calculando' }})
        </button>
      </div>

      <div class="col-md-6 mt-2" style="display: inline-flex; align-items: center; justify-content: flex-end;">
        <small class="mr-2" style="white-space: nowrap">Ordenar por:</small>
        <select v-model="order" @change="updateOrder(order)" class="browser-default custom-select" style="max-width: 250px;">
          <option :value="['data_emissao', 'desc']">Data de emissão</option>
          <option :value="['created_at', 'desc']">Data de criação</option>
        </select>
      </div>
    </div>

    <!-- Table -->
    <div class="row mt-2">
      <div class="col-md-12">

        <div class="wrap-table">
          <table class="table table-borderless table-dfe">
            <thead>
              <tr>
                <th>
                  <BkpsCheckBox :value="checkAllRow"
                                @input="changeCheckAllRow($event)" />
                </th>
                <th>Chave</th>
                <th>Numero</th>
                <th>Origem</th>
                <th>Emitente CNPJ</th>
                <th>Emitente Nome</th>
                <th>Prestador CNPJ</th>
                <th>Prestador Nome</th>
                <th>Tomador CNPJ</th>
                <th>Tomador Nome</th>
                <th>Data de Emissao</th>
                <th>Valor</th>
                <th>Criado em</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="nfse in nfseNacionais" :key="nfse.id">
                <td>
                  <BkpsCheckBox v-model="nfse.checked"
                                @input="changeCheckRow($event)" />
                </td>
                <td class="nowrap">
                  {{ nfse.chave | key }}
                  <copy-icon v-if="nfse.chave" @click="copyChave(nfse.chave)" size="1x" style="cursor: pointer" class="ml-1"/>
                </td>
                <td>{{ nfse.numero }}</td>
                <td>{{ cityName(nfse.origem) }}</td>
                <td class="nowrap">
                  {{ nfse.emitenteCnpj | cnpj }}
                  <copy-icon v-if="nfse.emitenteCnpj" @click="copyCnpj(nfse.emitenteCnpj)" size="1x" style="cursor: pointer" class="ml-1"/>
                </td>
                <td>{{ decodeEntities(nfse.emitenteNome) }}</td>
                <td class="nowrap">
                  {{ nfse.prestadorCnpj | cnpj }}
                  <copy-icon v-if="nfse.prestadorCnpj" @click="copyCnpj(nfse.prestadorCnpj)" size="1x" style="cursor: pointer" class="ml-1"/>
                </td>
                <td>{{ decodeEntities(nfse.prestadorNome) }}</td>
                <td class="nowrap">
                  {{ nfse.tomadorCnpj | cnpj }}
                  <copy-icon v-if="nfse.tomadorCnpj" @click="copyCnpj(nfse.tomadorCnpj)" size="1x" style="cursor: pointer" class="ml-1"/>
                </td>
                <td>{{ decodeEntities(nfse.tomadorNome) }}</td>
                <td>{{ nfse.dataEmissao | datetime }}</td>
                <td>{{ nfse.valorCents | centsToPrice }}</td>
                <td>{{ nfse.createdAt | datetime }}</td>
                <td>
                  <eye-icon @click="openXmlModal(nfse)" size="1.2x" style="cursor: pointer" class="mr-2"/>
                  <download-icon @click="downloadXml(nfse)" size="1.2x" style="cursor: pointer"/>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal XML -->
    <Modal v-model="showModalXml" :with-footer="false" width="xl">
      <h3 slot="header" class="my-2">XML</h3>
      <pre><code v-text="formattedXml"></code></pre>
    </Modal>

  </div>
</template>

<script>
  import { ChevronDownIcon, CopyIcon, DownloadIcon, EyeIcon, RepeatIcon, SearchIcon } from 'vue-feather-icons'
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import Dropdown from '@/components/shares/specialTag/Dropdown'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import Modal from '@/components/shares/specialTag/Modal'
  import Select2 from '@/components/shares/Select2'
  import CheckedModels from '@/mixins/CheckedModels'
  import ModelMapper from '@/mixins/ModelMapper'
  import handleSearchable from '@/models/handleSearchable'
  import DownloadCreator from '@/models/DownloadCreator'
  import { Filter } from '@/models/filter'
  import cities from '@/config/cities.json'

  const citiesMap = Object.fromEntries(cities.map(([code, name, uf]) => [code, `${name}/${uf}`]))

  const keyOrderby = 'nfseNacionalOrderBy'
  const fiveYearsInMinutes = 60 * 24 * 30 * 12 * 5

  export default {
    components: { BadgeFilters, BkpsCheckBox, ChevronDownIcon, CopyIcon, DownloadIcon, Dropdown, EyeIcon, Modal, Pagination, RepeatIcon, SearchIcon, Select2 },
    computed: {
      currentAxios() {
        return this.axios.v3
      },
      enableDownload() {
        return this.checkAll || this.nfseNacionais.some(item => item.checked)
      },
      nfseNacionais() {
        return this.getModels || []
      }
    },
    created() {
      const order = this.$ls.get(keyOrderby)
      this.order = order || ['data_emissao', 'desc']
      this.$set(this.query, 'periodType', 'data_emissao')
      this.defaultModelSetting.updateQueryURL = true
      this.$set(this.query, 'generic', '')
      this.asyncTemplateSelect({
        byURL: true,
        pagination: true,
        finish: () => this.loadOrigens()
      })
    },
    data() {
      return {
        filterEndDate: new Filter({
          key: 'endDate',
          label: 'Data final',
          type: 'date'
        }),
        filterStartDate: new Filter({
          key: 'startDate',
          label: 'Data inicial',
          type: 'date'
        }),
        filterOrigem: null,
        formattedXml: '',
        modelPath: 'dfe/documentos-fiscais',
        order: ['data_emissao', 'desc'],
        nameModel: 'NfseNacional',
        showFilter: false,
        showModalXml: false,
      }
    },
    methods: {
      buildParams(pagination) {
        if (this.query.generic)
          this.query.generic = handleSearchable(this.query.generic)

        return ModelMapper.methods.buildParams.call(this, pagination)
      },
      cityName(code) {
        return citiesMap[code] || code
      },
      changeCheckAll() {
        this.changeCheck({ checkAll: !this.checkAll, sources: this.nfseNacionais })
      },
      changeCheckAllRow(value) {
        this.changeCheck({ checkAllRow: value, sources: this.nfseNacionais })
      },
      changeCheckRow() {
        this.changeCheck({ sources: this.nfseNacionais })
      },
      copyCnpj(cnpj) {
        const sanitized = String(cnpj).replace(/[^0-9A-Za-z]/g, '')
        navigator.clipboard.writeText(sanitized)
          .then(() => this.$notify.add('CNPJ copiado!'))
          .catch(() => this.$notify.addError('Erro ao copiar CNPJ'))
      },
      copyChave(chave) {
        navigator.clipboard.writeText(chave)
          .then(() => this.$notify.add('Chave copiada!'))
          .catch(() => this.$notify.addError('Erro ao copiar chave'))
      },
      downloadXmlBatch() {
        const selectedItems = this.nfseNacionais.filter(item => item.checked)

        if (!this.checkAll && selectedItems.length === 0)
          return

        const q = this.checkAll
          ? this.buildParams().q || {}
          : { id: selectedItems.map(item => item.id) }

        this.openGlobalLoad()
        this.currentAxios.get(`${this.modelPath}/export-xmls`, { params: { type_format: 'xml', q } })
          .then(response => {
            if (response.data && response.data.link) {
              const link = document.createElement('a')
              link.href = response.data.link
              link.click()
              link.remove()
            } else if (response.meta.notice) {
              this.$notify.add(response.meta.notice, { closingTime: 12000 })
            }
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      downloadXml(nfse) {
        const filename = `nfse-${nfse.chave || nfse.numero || nfse.id}.xml`
        this.fetchXml(nfse)
          .then(xml => DownloadCreator.download(filename, xml))
          .catch(e => this.$notify.addError(e))
      },
      fetchXml(nfse) {
        return this.currentAxios.get(`${this.modelPath}/${nfse.chave}/xml`)
      },
      find() {
        this.asyncTemplateSelect()
      },
      generateReport() {
        let params = {}
        const selectedItems = this.nfseNacionais.filter(item => item.checked)

        if (this.checkAll)
          params = { q: this.buildParams().q || {} }
        else if (selectedItems.length > 0)
          params = { q: { id: selectedItems.map(item => item.id) } }
        else
          return

        this.openGlobalLoad()
        this.currentAxios.get(`${this.modelPath}/report`, { params })
          .then(response => {
            if (response.data && response.data.link) {
              const link = document.createElement('a')
              link.href = response.data.link
              link.target = '_blank'
              link.click()
              link.remove()
            } else if (response.meta.notice) {
              this.$notify.add(response.meta.notice, { closingTime: 12000 })
            }
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      loadOrigens() {
        this.currentAxios.get('dfe/documentos-fiscais/fetch_column/origem')
          .then(response => {
            this.filterOrigem = new Filter({
              key: 'origem',
              label: 'Origem',
              table: response.data.map(code => [citiesMap[code] || code, code])
            })
          })
      },
      updateOrder(order) {
        this.find()
        this.$ls.set(keyOrderby, order, { minutes: fiveYearsInMinutes })
      },
      decodeEntities(text) {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = text
        return textarea.value
      },
      formatXml(xml) {
        if (!xml) return ''
        xml = this.decodeEntities(xml)
        let formatted = ''
        let indent = ''
        const tab = '  '
        xml.split(/>\s*</).forEach(node => {
          if (node.match(/^\/\w/)) indent = indent.substring(tab.length)
          formatted += indent + '<' + node + '>\n'
          if (node.match(/^<?\w[^>]*[^/]$/) && !node.startsWith('?')) indent += tab
        })
        return formatted.substring(1, formatted.length - 2)
      },
      openXmlModal(nfse) {
        this.fetchXml(nfse)
          .then(xml => {
            this.formattedXml = this.formatXml(xml)
            this.showModalXml = true
          })
          .catch(e => this.$notify.addError(e))
      }
    },
    mixins: [CheckedModels, ModelMapper],
    name: 'NfseNacional'
  }
</script>
